import {
  type ScoringBand,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseDashboardThreadVerdictDto,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useGetMeiaStudy, useGetMeiaStudyMissions } from '@queries/meia.query';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import { CircleSlashIcon } from 'lucide-react';
import { type FC, type ReactNode, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import {
  getMeiaMissionCodeFromName,
  getMeiaMissionDesiredOutcome,
  getMeiaMissionTitleFromName,
} from './meia-mission-display.util';
import { MeiaMissionScopeCard } from './meia-mission-scope-card.component';
import { MeiaMissionThreadVerdictScore } from './meia-mission-thread-verdict-score.component';
import { InfoTooltip } from './meia-p4-dashboard-primitives.component';
import { formatDashboardScore, scoreFromBand } from './meia-p4-run-cases.util';
import { getScoringBandLabel, getScoringBandReadinessStatus } from './meia-scoring-band.util';
import { MeiaTaskReadinessStatusIcon } from './meia-task-readiness-status-icon.component';
import { MeiaThreadPickerCardShell } from './meia-thread-picker-card.component';

/**
 * Verdict-band status for picker cards: reuses the shared readiness icon for scored
 * bands (green/yellow/red) and falls back to a neutral gray icon while unscored.
 */
const MissionVerdictStatusIcon: FC<{ band?: ScoringBand | null }> = (props) => {
  const { band } = props;
  const status = getScoringBandReadinessStatus(band);

  if (!status) {
    return (
      <TooltipComponent
        side="top"
        trigger={
          <span
            aria-label="Mission verdict: not scored"
            className="mt-px inline-flex shrink-0"
          >
            <CircleSlashIcon className="size-5 text-gray-400" />
          </span>
        }
      >
        No scored verdict for this mission.
      </TooltipComponent>
    );
  }

  return (
    <MeiaTaskReadinessStatusIcon
      className="mt-px"
      label="Mission verdict"
      status={status}
      tooltip={`Verdict: ${getScoringBandLabel(band)}`}
    />
  );
};

type MissionScopePickerProps = {
  activeMissionId: string | null;
  canEdit?: boolean;
  missions: StudyRunCaseDashboardMissionDto[];
  onChange: (missionId: string) => void;
  studyId: string;
};

export const MissionScopePicker: FC<MissionScopePickerProps> = (props) => {
  const { activeMissionId, canEdit = false, missions, onChange, studyId } = props;
  const navigate = useNavigate();
  const { data: study } = useGetMeiaStudy({ studyId });
  const { data: liveMissions = [] } = useGetMeiaStudyMissions({ studyId });
  const kopProblem = study?.keyOperationalProblem?.trim();

  // The picker renders the run-case snapshot; live missions only enrich it with
  // display-only fields the snapshot lacks (the DMO excerpt).
  const liveMissionById = useMemo(() => new Map(liveMissions.map((mission) => [mission.id, mission])), [liveMissions]);

  const handleAddMissionInP1 = (): void => {
    navigate(meiaRoute.studyTab({ query: { action: 'addMission' }, studyId, tab: 'p1' }));
  };

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        button: canEdit ? { onClick: handleAddMissionInP1, text: '+ Add Mission in P1' } : undefined,
        title: (
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5">
              Mission Scope
              <InfoTooltip>
                Select which mission to inspect. Each mission keeps its own thread verdicts and stage scores.
              </InfoTooltip>
            </span>
            <span className="text-xs font-normal text-gray-400">
              ({missions.length} {missions.length === 1 ? 'mission' : 'missions'} on this KOP)
            </span>
          </span>
        ),
      }}
    >
      {kopProblem && <p className="line-clamp-2 text-xs text-gray-500">{kopProblem}</p>}
      <div className="flex flex-wrap gap-2">
        {missions.map((mission, index) => {
          const liveMission = liveMissionById.get(mission.missionId);

          return (
            <MeiaMissionScopeCard
              code={getMeiaMissionCodeFromName({ index, name: mission.missionName })}
              desiredOutcome={liveMission ? getMeiaMissionDesiredOutcome(liveMission) : ''}
              isSelected={activeMissionId === mission.missionId}
              key={mission.missionId}
              onSelect={() => onChange(mission.missionId)}
              statusIndicator={<MissionVerdictStatusIcon band={mission.summary.threadBand} />}
              threadCount={mission.threadVerdicts.length}
              title={getMeiaMissionTitleFromName(mission.missionName)}
            />
          );
        })}
      </div>
    </Tile>
  );
};

type ThreadVerdictPickerProps = {
  activeThreadId: string | null;
  canEdit?: boolean;
  missionId?: string | null;
  onChange: (threadId: string) => void;
  studyId: string;
  threads: StudyRunCaseDashboardThreadVerdictDto[];
};

export const ThreadVerdictPicker: FC<ThreadVerdictPickerProps> = (props) => {
  const { activeThreadId, canEdit = false, missionId, onChange, studyId, threads } = props;
  const navigate = useNavigate();

  const addThreadHref = missionId
    ? meiaRoute.studyTab({ query: { action: 'addThread', missionId }, studyId, tab: 'p2' })
    : meiaRoute.studyTab({ query: { action: 'addThread' }, studyId, tab: 'p2' });

  return (
    <Tile
      contentClassName="gap-3"
      emptyState={{
        action: canEdit ? (
          <Button
            asChild
            variant="link"
          >
            <Link to={addThreadHref}>Add Mission Thread</Link>
          </Button>
        ) : undefined,
        title: 'No Mission Threads',
      }}
      header={{
        button: canEdit ? { onClick: () => navigate(addThreadHref), text: '+ Add Thread in P2' } : undefined,
        title: (
          <span className="inline-flex items-center gap-1.5">
            Mission Threads
            <InfoTooltip>Select a Mission Thread to inspect its scored stages and measure performance.</InfoTooltip>
          </span>
        ),
      }}
      isEmpty={threads.length === 0}
    >
      <div className="flex flex-wrap gap-2">
        {threads.map((thread) => (
          <MeiaThreadPickerCardShell
            isSelected={activeThreadId === thread.threadId}
            key={thread.threadId}
            metaLine={getStudyThreadKillChainLabel(thread.killChainCategory)}
            onSelect={() => onChange(thread.threadId)}
            role={thread.threadRole}
            statusIndicator={
              <TooltipComponent
                side="top"
                trigger={
                  <span
                    aria-label={`Thread verdict score: ${formatThreadVerdictScore(thread)}`}
                    className="inline-flex shrink-0"
                  >
                    <MeiaMissionThreadVerdictScore
                      band={thread.band}
                      score={thread.score}
                    />
                  </span>
                }
              >
                {threadVerdictTooltip(thread)}
              </TooltipComponent>
            }
            title={thread.threadName}
          />
        ))}
      </div>
    </Tile>
  );
};

const formatThreadVerdictScore = (thread: StudyRunCaseDashboardThreadVerdictDto): string => {
  const score = thread.score ?? scoreFromBand(thread.band);
  if (score === null) return 'not scored';
  return `${formatDashboardScore(score)} out of 100`;
};

const threadVerdictTooltip = (thread: StudyRunCaseDashboardThreadVerdictDto): ReactNode => {
  const score = thread.score ?? scoreFromBand(thread.band);
  if (score === null) {
    return 'No scored verdict for this thread yet.';
  }

  return (
    <div className="flex max-w-[420px] flex-col gap-1">
      <div>
        <strong>Verdict Score:</strong> {formatDashboardScore(score)}/100
      </div>
      <div>
        <strong>Verdict:</strong> {thread.band ?? 'No score'}
      </div>
      <p className="mt-1 text-gray-600">
        This score comes from the latest stored scoring output for this mission thread.
      </p>
    </div>
  );
};
